/* eslint-disable import/first */
import loadable from '@loadable/component';

import React from 'react';
import {
  View,
  TouchableOpacity,
  GestureResponderEvent,
} from 'react-native';
import classNames from 'classnames';

import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import StateLink from '../../StateLink';
import HeaderMenu from './HeaderMenu';
import { Container as SidebarToggle } from '../../shared/SidebarToggle';
import Image from '../../shared/Image';
import Logo from '../../../../images/styleseat-logo-black.svg';
import { useResponsive } from '../../../hooks/useResponsiveEffect';
import analytics from '../../../modules/analytics';
import { ReferralEntryTrigger } from './ReferralEntryTrigger';

const ShareTrigger = loadable(() => (
  import('../sharing')
), { resolveComponent: mod => mod.ShareTrigger });

import {
  Absolute,
  BodyText,
  Touchable,
  AnimatedBadge,
} from '../../ui';
import NavSearchBar from '../NavSearchBar';
import './Header.module.scss';
import { BasicSearchIcon } from '../../ui/Icons';

export type HeaderProps = {
  /** The provider ID currently logged in, if any */
  providerId?: number;
  /** True to display the sidebar toggle. Default is true. */
  showSidebarToggle?: boolean;
  onClickSetUpMyBusiness: (e: GestureResponderEvent) => void;
  showSearch?: boolean;
  hideSetUpMyBusiness?: boolean;
  constrainMaxWidth?: boolean;
  showMobileSearch: boolean;
  onMobileSearchClick: () => void;
  showMobileSearchDot?: boolean;
  showSetUpBusinessLink?: boolean;
  /** True to show a bottom border on mobile, otherwise false. Default is true. */
  showMobileBorder?: boolean;
  showReferralEntry?: boolean;
  referralShareProps?: any;
  utmParams: any;
};

/**
 * Header - wrapping component to display the client header to anonymous and client users.
 *
 * Usage:
 * ```jsx
 * <Header />
 * ```
 *
 * This component takes no props and is connected to the redux store.
 */
export const HeaderBase: React.FC<HeaderProps> = ({
  providerId,
  showSidebarToggle = true,
  onClickSetUpMyBusiness,
  showSearch = true,
  showMobileSearch,
  onMobileSearchClick,
  showMobileSearchDot = false,
  showSetUpBusinessLink = false,
  showMobileBorder = true,
  showReferralEntry = false,
  referralShareProps,
  utmParams = {},
}) => {
  const { mobile, tablet } = useResponsive();

  React.useEffect(() => {
    if (showReferralEntry) {
      analytics.track('client_header_referral_entry_viewed');
    }
  }, [showReferralEntry]);

  const referralEntry = React.useMemo(() => (
    <ShareTrigger
      {...referralShareProps}
      renderTrigger={onClick => (
        <ReferralEntryTrigger
          onClick={onClick}
          appearance={mobile || tablet ? 'normal' : 'header'}
        />
      )}
    />
  ), [referralShareProps, mobile, tablet]);

  return (
    <View styleName="styleseat-header-component">
      <View
        testID="HeaderUI"
        styleName={classNames('styleseat-header', {
          noBorder: !showMobileBorder,
        })}
      >
        <View styleName="left-right-bar-wrapper">
          <View styleName="leftBar">
            {showSidebarToggle && (
              <View styleName="styleseat-header__toggleWrapper">
                <SidebarToggle appearance="light" />
              </View>
            )}
            <StateLink to="home" testID="header-logo" params={utmParams}>
              <Image
                source={Logo}
                styleName="styleseat-header__styleseat-logo"
              />
            </StateLink>
            <View />
            {showSearch && (
              <View styleName="search-bar-wrapper">
                <NavSearchBar />
              </View>
            )}
            {showSetUpBusinessLink && (
              <Inline
                alignItems="center"
                paddingRight={20}
              >
                <TouchableOpacity
                  onPress={onClickSetUpMyBusiness}
                  testID="set-up-my-business-link"
                >
                  <Inline paddingRight={2}>
                    <BodyText
                      color={colors.punch}
                      appearance="normal"
                      textAlign="center"
                      weight="bold"
                    >
                      {'Set Up My\nBusiness'}
                    </BodyText>
                  </Inline>
                </TouchableOpacity>
              </Inline>
            )}
            {(mobile || tablet) && (showMobileSearch || showReferralEntry) && (
              <Inline paddingY={8} alignItems="flex-end">
                {showReferralEntry && (
                  <Box paddingX={12} paddingBottom={8}>
                    {referralEntry}
                  </Box>
                )}
                {showMobileSearch && (
                  <Touchable
                    onClick={onMobileSearchClick}
                    testID="mobile-header-search-icon"
                  >
                    <Box
                      alignItems="center"
                      flexDirection="column"
                      justifyContent="center"
                      paddingBottom={12}
                      paddingRight={4}
                    >
                      <Absolute
                        top={0}
                        right={12}
                      >
                        <AnimatedBadge
                          appearance="client"
                          show={showMobileSearchDot}
                          testID="mobile-header-search-dot"
                        />
                      </Absolute>
                      <Box
                        paddingRight={12}
                      >
                        <BasicSearchIcon />
                      </Box>
                    </Box>
                  </Touchable>
                )}
              </Inline>
            )}
          </View>
          <HeaderMenu
            providerId={providerId}
            referralEntry={referralEntry}
            showReferralEntry={showReferralEntry}
          />
        </View>
      </View>
    </View>
  );
};

export default HeaderBase;
